import {
  Children,
  ComponentPropsWithRef,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from "react";
import { useMediaQuery } from "usehooks-ts";
import { cn } from "web-design-system";
import { PageNavbar, PageNavbarItem } from "./page-navbar";

function PageHeader({
  title,
  actions,
  className,
}: {
  title: string | React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  return (
    <div
      className={cn(
        "flex items-center z-20 bg-background px-5 pt-2 gap-2 justify-between sticky top-0 w-full",
        className
      )}
    >
      {typeof title === "string" ? (
        <h4 className={cn(isDesktop ? "w-70" : undefined)}>{title}</h4>
      ) : (
        title
      )}
      <div className="flex flex-row justify-end h-15 flex-1 items-center w-full gap-2">
        {actions}
      </div>
    </div>
  );
}

interface PageBodyProps {
  children: ReactNode;
  className?: string;
}

function PageBody({ children, className }: PageBodyProps) {
  return (
    <div className={cn("px-5 flex rounded-lg flex-col pt-2 flex-1", className)}>
      {children}
    </div>
  );
}

function findSlot(
  children: ReactNode,
  Slot: React.ElementType
): ReactElement | undefined {
  const match = Children.toArray(children).find(
    (child): child is ReactElement =>
      isValidElement(child) && child.type === Slot
  );
  return match;
}

export function Page(props: ComponentPropsWithRef<"div">) {
  const sidebar = findSlot(props.children, PageSidebar);
  const statusBar = findSlot(props.children, PageStatusBar);
  const content = findSlot(props.children, PageContent);

  return (
    <div className={cn("flex flex-row rounded-lg size-full gap-2 relative")}>
      {sidebar}
      <div
        className={cn("flex flex-col flex-1 gap-2 rounded-lg overflow-hidden")}
      >
        {statusBar}
        {content}
      </div>
    </div>
  );
}

function PageSidebar(props: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        "w-sidebar bg-card rounded-lg p-5 flex flex-col h-full shrink-0",
        props.className
      )}
    >
      {props.children}
    </div>
  );
}

function PageStatusBar(props: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        "bg-card flex w-full rounded-lg shrink-0 empty:hidden",
        props.className
      )}
    >
      {props.children}
    </div>
  );
}

function PageContent({
  className,
  children,
  ...rest
}: ComponentPropsWithRef<"div">) {
  return (
    <div
      className={cn(
        "flex flex-col rounded-lg bg-background pb-4 flex-1 overflow-auto",
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

Page.Header = PageHeader;
Page.Body = PageBody;
Page.Content = PageContent;
Page.Sidebar = PageSidebar;
Page.Navbar = PageNavbar;
Page.NavbarItem = PageNavbarItem;
Page.StatusBar = PageStatusBar;
